import React, { useEffect, useState } from 'react';
import {
  Column,
  Button,
  Columns,
  Notification,
} from '@codeparva/ui-components-library';
import { CardNumberField } from '../FormFields/CardNumberField';
import {
  CustomFormattedInputField,
  CustomTextField,
  CustomPostalCodeField,
  CustomCvvField,
} from '../FormFields/GeneralFormFields';
import { maxLengths } from '../../constants/accountPanelConfigs';
import { expiryDateFormatter } from '../../helpers/cardInfoValidator';
import { useFormikContext, Form } from 'formik';
import { AccountPanelTypes } from '../../AccountPanelTypes';
import { HOSTED_FORM_ID } from '../../../../components/syrapayHostedForm/Constants';
import SyrapayHostedForm from '../../../../components/syrapayHostedForm/SyrapayHostedForm';
import { getPaymentMethodInfo } from '../../helpers/PaymentHelpers';
import { weakKey } from '../../../../utilities/weakKey';
import { getAsterisk } from './ErrorLabel';

export type TUpdatePaymentDetailsForm = {
  disable: boolean;
  shouldShowHostedForm: boolean;
  setDisabled: any;
  setShouldShowHostedForm: any;
  authKey: any;
  callBackHandler: any;
  paymentMethod: any;
  cardProcessorId: any;
};

export const UpdatePaymentDetailsForm = ({
  disable,
  shouldShowHostedForm,
  setShouldShowHostedForm,
  setDisabled,
  authKey,
  callBackHandler,
  paymentMethod,
  cardProcessorId,
}: TUpdatePaymentDetailsForm) => {
  const { cvv, postalCode, expiryDate } = maxLengths;
  const { handleSubmit, values } = useFormikContext<
    AccountPanelTypes.TCardDetailsFormInitialValues
  >();

  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('default');
  const [disablePaymentButton, setDisablePaymentButton] = useState(false);
  const [notification, setNotification] = React.useState(false);
  const [isSuccess, setIsSuccess] = React.useState(false);

  useEffect(() => {
    if (isSuccess) {
      formCancellationHandler();
      setSelectedPaymentMethod('default');
    }
  }, [isSuccess]);

  useEffect(() => {
    if (selectedPaymentMethod === 'default') {
      setDisablePaymentButton(true);
    } else {
      setDisablePaymentButton(false);
    }
  }, [selectedPaymentMethod]);
  const formCancellationHandler = () => {
    setShouldShowHostedForm(false);
    setDisabled(false);
  };
  const handleChange = (e: any) => {
    const filteredPaymentMethod = paymentMethod.data.filter(
      (indivPaymentMethod: any) => indivPaymentMethod.value.modifier
    );
    const selectedPaymentMethod = filteredPaymentMethod.find(
      (item: any) => item.id === e.target.value
    );
    if (e.target.value !== 'default') {
      setSelectedPaymentMethod(
        getPaymentMethodInfo(selectedPaymentMethod, 'id')
      );
    }
  };
  const PaymentMethodSelectionDropDown = () => {
    return (
      <>
        <label className="cardLabel">
          Select Card Type
          {getAsterisk()}
        </label>
        <div
          className="select is-large is-primary"
          style={{
            marginBottom: '1rem',
            width: '100%',
          }}
        >
          <select
            value={selectedPaymentMethod}
            onChange={handleChange}
            className="dropdown"
          >
            <option value="default">Select a Payment Method</option>
            {paymentMethod.data.map((indivPaymentMethod: any) => {
              const payment_methodId = getPaymentMethodInfo(
                indivPaymentMethod,
                'id'
              );
              return (
                indivPaymentMethod.value.type === 'Credit_Card' &&
                indivPaymentMethod.value.status !== 'Inactive' && (
                  <option
                    value={payment_methodId}
                    key={weakKey({ id: payment_methodId })}
                  >
                    {indivPaymentMethod.value.description}
                  </option>
                )
              );
            })}
          </select>
        </div>
      </>
    );
  };
  const hostedFormSubmitHandler = async (
    _transaction_id: any,
    _token_to_save: any,
    otherResponse: any
  ) => {
    const {
      gatewayResponse: { token },
      requestDetails: {
        expirationMonth,
        expirationYear,
        address1,
        zip,
        nameOnAccount,
        cardNumber,
      },
    } = otherResponse;
    const financialsBody = {
      preferred_payment_method_id: selectedPaymentMethod,
      card_info: {
        token: token,
        card_processor_id: cardProcessorId,
        masked_card_number: cardNumber,
        expiration_date: {
          month: expirationMonth,
          year: expirationYear
            .toString()
            .slice(
              expirationYear.toString().length - 2,
              expirationYear.toString().length
            ),
        },
        billing_info: {
          name: nameOnAccount,
          address: {
            line1: address1,
            zip_code: zip,
          },
        },
      },
    };
    callBackHandler(financialsBody, setNotification, setIsSuccess);
  };

  return (
    <Form id={HOSTED_FORM_ID}>
      <Column paddingless marginless>
        <div className="cardInfoContainer uneditableSection">
          <div className="cardNumberSection">
            <CardNumberField
              name="cardNumber"
              label="Credit Card Number"
              fieldname="cardNumber"
              readOnly
            />
          </div>
          <Columns className="twoInputsSection">
            <Column>
              <CustomFormattedInputField
                label="Expiry Date"
                formattingFunction={expiryDateFormatter}
                fieldname="expiryDate"
                name="expiryDate"
                maxLength={expiryDate}
                placeholder="MM/YY"
                type="text"
                expression="[^0-9/\s]+"
                readOnly
              />
            </Column>
            <Column>
              <CustomCvvField
                name="cvv"
                fieldname="cvv"
                maxLength={cvv}
                label="CVV"
                type="password"
                autoComplete="off"
                cardNumber={values.cardNumber}
                readOnly
              />
            </Column>
          </Columns>
          <Column className="nameSection">
            <CustomTextField
              label="Name"
              name="holderName"
              fieldname="holderName"
              placeholder="Joe Doe"
              type="text"
              readOnly
            />
          </Column>
          <Column className="addressSection">
            <CustomTextField
              label="Address Line 1"
              name="address"
              placeholder="Address"
              type="text"
              readOnly
            />
          </Column>
          <Column className="postalCodeSection">
            <CustomPostalCodeField
              label="Postal Code"
              name="postalCode"
              fieldname="postalCode"
              placeholder="Postal Code"
              type="text"
              maxLength={postalCode}
              readOnly
            />
          </Column>
          <div className="overlaySection"></div>
        </div>
      </Column>
      <h5 className="py-5">Update Card Information</h5>
      <Column paddingless marginless className="dropdownSection">
        {notification ? (
          <div className="notificationSection">
            <Notification className="notificationColor">
              Card Details has been updated successfully.
              <button
                className="delete"
                onClick={() => setNotification(false)}
              ></button>
            </Notification>
          </div>
        ) : (
          <></>
        )}
        <PaymentMethodSelectionDropDown />
      </Column>
      <Button
        size="medium"
        type="button"
        onClick={(e: any) => handleSubmit(e)}
        disabled={disable || disablePaymentButton}
        className={`updatePaymentDetailsBtn ${
          shouldShowHostedForm ? 'is-loading' : ''
        }`}
      >
        Edit Details
      </Button>
      {shouldShowHostedForm ? (
        <SyrapayHostedForm
          amount="0"
          shouldSaveCard={true}
          hostedFormSubmitHandler={hostedFormSubmitHandler}
          paymentCancellationHandler={formCancellationHandler}
          authKey={authKey}
          tokenOnly={true}
        />
      ) : (
        <></>
      )}
    </Form>
  );
};
